import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Landmark, RefreshCw, ShieldCheck, FileText, Link2, Rocket, Settings } from "lucide-react";

function printReport(report, baseline) {
  const es = report.executive_summary || {};
  const kpi = report.executive_kpi_snapshot || {};
  const ops = report.operational_readiness || {};
  const iv = report.integrity_verification || {};
  const conn = ops.connector_status || {};
  const row = (k, v) => `<tr><td style="padding:6px 12px;color:#6b7280">${k}</td><td style="padding:6px 12px;font-weight:600">${v ?? "—"}</td></tr>`;
  const connRows = Object.entries(conn).map(([k, v]) =>
    `<tr><td style="padding:6px 12px;text-transform:capitalize">${k}</td><td style="padding:6px 12px">${v.connector}</td><td style="padding:6px 12px;font-weight:600;color:${v.status === "live" ? "#1D9E75" : "#BA7517"}">${v.status.toUpperCase()}</td></tr>`).join("");
  const html = `<!doctype html><html><head><title>${report.report_id}</title>
  <style>body{font-family:Georgia,'Times New Roman',serif;color:#0E0A1F;max-width:820px;margin:32px auto;padding:0 24px}
  h1{font-size:28px;margin:0;color:#1F75A8}h2{font-size:15px;text-transform:uppercase;letter-spacing:.08em;color:#1F75A8;border-bottom:2px solid #EAF7FF;padding-bottom:6px;margin-top:28px}
  table{width:100%;border-collapse:collapse;font-size:13px}.hdr{border-bottom:3px solid #1F75A8;padding-bottom:12px}
  .badge{display:inline-block;background:#EAF7FF;color:#1F75A8;padding:4px 12px;border-radius:16px;font-size:12px;font-weight:700}
  .foot{margin-top:36px;border-top:1px solid #ddd;padding-top:12px;font-size:12px;color:#6b7280;text-align:center}</style></head><body>
  <div class="hdr"><h1>Executive Production Baseline Report</h1>
  <div style="margin-top:6px;font-size:13px;color:#6b7280">${baseline.label} · ${report.report_id}</div>
  <div style="margin-top:8px"><span class="badge">${(ops.production_certification_status||"")} · Score ${ops.certification_score ?? "—"}%</span></div></div>
  <h2>Executive Summary</h2><table>
  ${row("Production Baseline ID", es.baseline_id)}${row("Certification ID", es.certification_id)}
  ${row("Deployment Fingerprint", es.deployment_fingerprint)}${row("Archive ID", es.archive_id)}
  ${row("Launch Version", es.launch_version)}${row("Launch Date", es.launch_date)}
  ${row("Environment", es.environment)}${row("Build Version", es.build_version)}
  ${row("Git Commit", es.git_commit)}${row("Timestamp", es.timestamp)}</table>
  <h2>Executive KPI Snapshot</h2><table>
  ${row("Executive Intelligence Index", kpi.executive_intelligence_index)}
  ${row("Revenue source", (kpi.revenue||{}).source)}${row("Journey — succeeded payments", (kpi.journey||{}).succeeded_payments)}</table>
  <h2>Operational Readiness — Connectors</h2><table>${connRows}</table>
  <h2>Operational Readiness Summary</h2><table>
  ${row("Production Certification", ops.production_certification_status)}${row("Environment", ops.environment)}
  ${row("Deployment Status", ops.deployment_status)}${row("Security Status", ops.security_status)}</table>
  <h2>Integrity Verification</h2><table>
  ${row("SHA256 Hash", iv.sha256_hash)}${row("Verification Status", iv.verification_status)}
  ${row("Archive Checksum", iv.archive_checksum)}${row("Generated At", iv.generated_at)}</table>
  <div class="foot"><strong>Marrsing Executive Operating System™</strong><br/>Executive Intelligence Platform</div>
  </body></html>`;
  const w = window.open("", "_blank");
  w.document.write(html); w.document.close();
  setTimeout(() => w.print(), 400);
}

export default function BaselinePanel() {
  const [baselines, setBaselines] = useState([]);
  const [config, setConfig] = useState(null);
  const [chain, setChain] = useState(null);
  const [verify, setVerify] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [edit, setEdit] = useState(false);
  const [cfgForm, setCfgForm] = useState({ launch_date: "", launch_name: "", launch_version: "" });

  const load = useCallback(async () => {
    const [b, c] = await Promise.all([api.get("/admin/production-baselines"), api.get("/admin/system-config")]);
    setBaselines(b.data.items); setConfig(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const capture = async (kind, label) => {
    if (!window.confirm(`Capture immutable baseline: "${label}"?`)) return;
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post("/admin/production-baselines", { label, baseline_kind: kind });
      setMsg(`Captured ${data.baseline_id} (cert ${data.certification_score}%, ${data.certified ? "CERTIFIED" : "NOT CERTIFIED"})`);
      await load();
    } catch (e) { setMsg("Baseline capture failed."); }
    setBusy(false);
  };
  const viewReport = async (id, b) => {
    const { data } = await api.get(`/admin/production-baselines/${id}/report`);
    printReport(data, b);
  };
  const runVerify = async (id) => { const { data } = await api.get(`/admin/production-baselines/${id}/verify`); setVerify(data); };
  const viewChain = async (id) => { const { data } = await api.get(`/admin/production-baselines/${id}/chain`); setChain(data); };

  const saveConfig = async () => {
    setBusy(true); setMsg("");
    try {
      await api.put("/admin/system-config", cfgForm);
      setEdit(false);
      setMsg(`Launch date set to ${cfgForm.launch_date} (Day Zero).`);
      await load();
    } catch (e) { setMsg("Could not update launch configuration."); }
    setBusy(false);
  };

  const operationalReset = async () => {
    if (!window.confirm("OPERATIONAL RESET: archive all operational counters and switch MEOS to LIVE mode? History is preserved; dashboards reset to clean baseline. This is irreversible without restore.")) return;
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post("/admin/operational-reset", { confirm: true });
      setMsg(`Operational reset complete → live mode. Archived to ${data.archived_to}.`);
      await load();
    } catch (e) { setMsg("Operational reset failed."); }
    setBusy(false);
  };

  return (
    <div data-testid="baseline-panel">
      <div className="toolbar">
        <div className="section-label" style={{ margin: 0 }}>
          <Landmark size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} /> Production Baselines — Executive Governance
        </div>
        <div className="spacer" />
        <button className="btn-ghost" onClick={load} disabled={busy}><RefreshCw size={13} /> Refresh</button>
      </div>

      {msg && <div className="card" data-testid="baseline-msg" style={{ borderColor: "#1F75A8", color: "#1F75A8" }}>{msg}</div>}

      {config && (
        <div className="card" data-testid="launch-config" style={{ marginBottom: 16 }}>
          <div className="toolbar" style={{ marginTop: 0 }}>
            <div className="detail-label" style={{ margin: 0 }}><Settings size={12} style={{ verticalAlign: "-1px" }} /> Launch configuration</div>
            <div className="spacer" />
            <button className="btn-ghost" onClick={() => { setEdit(!edit); setCfgForm({ launch_date: config.launch_date, launch_name: config.launch_name, launch_version: config.launch_version }); }} data-testid="edit-launch-config">{edit ? "Cancel" : "Edit"}</button>
          </div>
          {!edit ? (
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 8, fontSize: 13 }}>
              <span>Launch date (Day Zero): <strong>{config.launch_date}</strong></span>
              <span>Name: <strong>{config.launch_name}</strong></span>
              <span>Version: <strong>{config.launch_version}</strong></span>
              <span>Mode: <strong style={{ color: config.operational_mode === "live" ? "#1D9E75" : "#BA7517" }}>{(config.operational_mode || "").toUpperCase()}</strong></span>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 8, marginTop: 8, maxWidth: 420 }}>
              <label style={{ fontSize: 12 }}>Launch date (Day Zero)
                <input type="date" className="input" value={cfgForm.launch_date} onChange={(e) => setCfgForm({ ...cfgForm, launch_date: e.target.value })} data-testid="launch-date-input" /></label>
              <input className="input" placeholder="Launch name" value={cfgForm.launch_name} onChange={(e) => setCfgForm({ ...cfgForm, launch_name: e.target.value })} />
              <input className="input" placeholder="Launch version" value={cfgForm.launch_version} onChange={(e) => setCfgForm({ ...cfgForm, launch_version: e.target.value })} />
              <button className="btn-primary" onClick={saveConfig} disabled={busy} data-testid="save-launch-config">Save launch date</button>
            </div>
          )}
        </div>
      )}

      <div className="toolbar" style={{ marginTop: 0 }}>
        <button className="btn-primary" onClick={() => capture("technical", "Production Baseline v1.0 – 100% Launch Certified")} disabled={busy} data-testid="capture-technical-baseline">
          <ShieldCheck size={14} /> Capture v1.0 (Technical)
        </button>
        <button className="btn-primary" onClick={() => capture("business", "Official Business Launch Baseline")} disabled={busy} data-testid="capture-business-baseline">
          <Rocket size={14} /> Capture Launch Baseline
        </button>
        <div className="spacer" />
        <button className="btn-ghost" onClick={operationalReset} disabled={busy} data-testid="operational-reset-button" style={{ color: "#C0392B" }}>
          <Rocket size={13} /> Operational reset
        </button>
      </div>

      <div className="section-label">Captured baselines ({baselines.length})</div>
      {baselines.length === 0 && <div className="empty-state" data-testid="baseline-empty">No baselines captured yet.</div>}
      <div style={{ display: "grid", gap: 10 }}>
        {baselines.map((b) => (
          <div key={b.baseline_id} className="card" data-testid={`baseline-row-${b.baseline_id}`}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontWeight: 600 }}>{b.label}</div>
                <div style={{ fontSize: 12, opacity: 0.7 }}>{b.baseline_id} · {b.launch_version} · cert {b.certification_score}% · {new Date(b.created_at).toLocaleString()}</div>
                <div style={{ fontSize: 11, opacity: 0.55, marginTop: 2, wordBreak: "break-all" }}>{b.integrity_hash}</div>
              </div>
              <span className={`cert-badge ${b.certified ? "cert-yes" : "cert-no"}`} style={{ fontSize: 11 }}>{b.certified ? "CERTIFIED" : `${b.certification_score}%`}</span>
              <button className="btn-ghost" onClick={() => viewReport(b.baseline_id, b)} data-testid={`report-${b.baseline_id}`}><FileText size={13} /> Report (PDF)</button>
              <button className="btn-ghost" onClick={() => runVerify(b.baseline_id)} data-testid={`verify-baseline-${b.baseline_id}`}><ShieldCheck size={13} /> Verify</button>
              <button className="btn-ghost" onClick={() => viewChain(b.baseline_id)} data-testid={`chain-${b.baseline_id}`}><Link2 size={13} /> Chain</button>
            </div>
            {verify && verify.baseline_id === b.baseline_id && (
              <div data-testid="baseline-verify-result" style={{ marginTop: 8, fontSize: 12, color: verify.integrity_ok ? "#1D9E75" : "#C0392B" }}>
                Integrity: <strong>{verify.status}</strong>
              </div>
            )}
          </div>
        ))}
      </div>

      {chain && (
        <div className="card" data-testid="chain-detail" style={{ marginTop: 16 }}>
          <div className="toolbar" style={{ marginTop: 0 }}>
            <div className="detail-label" style={{ margin: 0 }}>Chain of custody — {chain.baseline.baseline_id}</div>
            <div className="spacer" />
            <button className="btn-ghost" onClick={() => setChain(null)}>Close</button>
          </div>
          {Object.entries(chain.chain_of_custody).map(([k, v]) => (
            <div key={k} style={{ fontSize: 12, padding: "4px 0", borderBottom: "1px solid #eee", textTransform: "capitalize" }}>
              <strong>{k.replace(/_/g, " ")}</strong>: {typeof v === "object" ? JSON.stringify(v) : String(v)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
